<template>
  <EmailPasswordLoginDialog
    region="overseas"
    :visible="pageVisible"
    :handle-ok="props.handleOk"
    :handle-cancel="props.handleCancel"
    :remove="props.remove"
    :right-type="props.rightType"
    :create_type="props.create_type"
    :source="props.source"
    :signup_source="props.signup_source"
    :initial-email="props.initialEmail"
    :submit-email-password="submitEmailPassword"
    :continue-with-google="continueWith"
    :facebook-login="facebookLogin"
    :discord-login="discordLogin"
    :apple-login="signInAppleLogin"
  />
</template>

<script setup>
/* eslint-disable vue/prop-name-casing */
import { onMounted, ref } from "vue";

import apiUser from "@/api/api/apiUser.js";
import { getLatestUtmSourceFromCookie } from "@/api/net/config.js";
import message from "@/components/functionCallComponent/message/message.js";
import EmailPasswordLoginDialog from "@/components/functionComp/components/EmailPasswordLoginDialog.vue";
import useUserDataStore from "@/store/userDataStore";
import { firebaseUtils } from "@/utils/analytic/firebaseUtils.js";

const isRecoveraccount = window.location.pathname.includes("/recoveraccount");

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  handleOk: {
    type: Function,
    default: null,
  },
  handleCancel: {
    type: Function,
    default: null,
  },
  remove: {
    type: Function,
    default: null,
  },
  rightType: {
    type: String,
    default: "login",
  },
  create_type: {
    type: String,
    default: "2",
  },
  source: {
    type: String,
    default: "other",
  },
  signup_source: {
    type: String,
    default: "",
  },
  initialEmail: {
    type: String,
    default: "",
  },
});

const pageVisible = ref(props.visible);
const userDataStore = useUserDataStore();

const markSignupSource = () => {
  if (props.signup_source) {
    sessionStorage.setItem("signup_source", props.signup_source);
  }
};

const submitEmailPassword = async ({
  account,
  password,
  intent = "signin",
}) => {
  const response = await apiUser.emailPasswordLogin({
    mode: intent === "signin" ? "email" : "otp_email",
    email: account,
    password,
    source: getLatestUtmSourceFromCookie() ?? "",
    signup_source: userDataStore.getSignupSource(props.signup_source),
    activity_source: userDataStore.getActivitySource(),
  });

  const registrationErrors = {
    9016: "Email already registered. Please sign in.",
    9021: "The account already exists. Please input email address and password to log in.",
  };

  const sendVerificationEmail = async () => {
    const verifyRes = await apiUser.requestEmailVerification({
      email: account,
      source: "web",
    });
    if (verifyRes?.code !== 200) {
      message.error({
        maskClosable: true,
        position: "top",
        content:
          registrationErrors[verifyRes?.code] ||
          "Failed to send verification email. Please try again later.",
      });
      return false;
    }
    return { sentVerification: true };
  };

  if (response?.code === 200 && intent === "signup") {
    const hasLoginData = Boolean(
      response?.data?.auth || response?.data?.session_token
    );
    if (!hasLoginData) {
      return sendVerificationEmail();
    }

    return {
      fieldErrors: {
        account: "An account already exists with this email. Please sign in.",
      },
    };
  }

  if (response?.code === 200) {
    userDataStore.updateAuthData(response.data);
    if (typeof props.handleOk === "function") {
      props.handleOk(true);
    }
    return true;
  }

  if (registrationErrors[response?.code]) {
    return {
      fieldErrors: {
        account: registrationErrors[response.code],
      },
    };
  }

  if (
    response?.code === 404 ||
    response?.code === 3008 ||
    response?.code === 9008 ||
    response?.code === 9017 ||
    response?.code === 9025 ||
    response?.code === 9023
  ) {
    if (intent !== "signup") {
      return {
        fieldErrors: {
          account: "No account found. Please sign up.",
        },
      };
    }
    return sendVerificationEmail();
  }

  if (intent === "signin" && response?.code === 9022) {
    return {
      fieldErrors: {
        password: response?.msg || "Incorrect password",
      },
    };
  }

  if (response?.code === 4001 || response?.code === 3004) {
    return {
      fieldErrors: {
        [intent === "signup" ? "account" : "password"]:
          intent === "signup"
            ? "An account already exists with this email. Please sign in."
            : "Incorrect password",
      },
    };
  }

  if (intent === "signup") {
    return {
      blocked: /invalid email address|disposable|temporary/i.test(
        response?.msg || ""
      ),
      isNewUser: Boolean(response?.data?.is_new_user ?? response?.is_new_user),
      fieldErrors: {
        account: "Invalid email address. Please try a different one.",
      },
    };
  }

  message.error({
    maskClosable: true,
    position: "top",
    content: "Sign in failed. Please try again later.",
  });
  return false;
};

const discordLogin = () => {
  markSignupSource();
  const clientId = "1271026704529362995";
  const url =
    window.location.pathname === "/recoveraccount"
      ? `${window.location.origin}/recoveraccount`
      : window.location.pathname === "/create"
        ? `${window.location.origin}/create`
        : `${window.location.origin}/`;
  const redirectUri = encodeURIComponent(url);
  const discordAuthUrl = `https://discord.com/oauth2/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=token&scope=identify`;

  window.location.href = discordAuthUrl;
};

const continueWith = async (state) => {
  firebaseUtils.logClickEvent(
    props.rightType === "generate" ? "newuser_login" : "login",
    {
      create_type: props.create_type,
      source: props.source,
      page_route: window.location.pathname,
    }
  );
  await userDataStore.threeLogin(
    state.x_auth_token,
    false,
    props.signup_source
  );
  if (typeof props.handleOk === "function") {
    props.handleOk(true);
  }
  pageVisible.value = false;
};

const facebookLogin = () => {
  markSignupSource();
  if (!window.FB?.login) return;
  window.FB.login(
    function (respond) {
      if (respond?.authResponse) {
        userDataStore
          .threeLogin(
            respond?.authResponse?.accessToken,
            false,
            props.signup_source,
            "facebook_web"
          )
          .then(() => {
            facebookLoginOut();
            if (typeof props.handleOk === "function") {
              props.handleOk(true);
            }
            pageVisible.value = false;
          });
      }
    },
    { scope: "public_profile,email" }
  );
};

const facebookLoginOut = () => {
  if (!window.FB?.getLoginStatus) return;
  window.FB.getLoginStatus(function (response) {
    if (response.status === "connected") {
      window.FB.logout();
    }
  });
};

const initAppleLogin = () => {
  if (window.AppleID?.auth) {
    const currentPath = window.location.pathname;
    let apiUrl = `${window.location.origin}/api/notify/ios`;
    if (currentPath === "/recoveraccount") {
      apiUrl += "?page=recoveraccount";
    }
    window.AppleID.auth.init({
      clientId: "ai.mureka.service",
      redirectURI: apiUrl,
    });
  }
};

const signInAppleLogin = () => {
  try {
    markSignupSource();
    if (window.AppleID?.auth?.signIn) {
      window.AppleID.auth.signIn();
    }
  } catch (error) {
    console.error(" Apple Sign In Error: ", error);
  }
};

onMounted(() => {
  if (!isRecoveraccount) {
    initAppleLogin();
  }
});

defineExpose({ remove: props.remove });
</script>
